import { useEffect, useMemo, useState } from 'react';
import { Save } from 'lucide-react';
import { api } from '../api.js';
import { Button, Field, FilterBar, Header, Modal, Pagination, Table, dateTime, queryString, useOpsContext, useQuery } from './ui.jsx';

const groupNames = {
  users: 'Người dùng', roles: 'Vai trò', branches: 'Cơ sở', customers: 'Khách hàng', bookings: 'Booking', cash_shifts: 'Ca tiền mặt',
  rewards: 'Danh mục quà', reward_inventory: 'Tồn kho quà', redemptions: 'Đổi quà', reports: 'Báo cáo', audit_logs: 'Nhật ký', station: 'OnePlay Station', schedule: 'Lịch làm', payroll: 'Tính lương',
};

export function AccessPage() {
  const { fail, success } = useOpsContext();
  const matrix = useQuery('/admin/permissions', { realtime: false });
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(null);
  useEffect(() => { if (matrix.data) setDraft(Object.fromEntries(matrix.data.roles.map((role) => [role.role_key, new Set(role.permissions)]))); }, [matrix.data]);
  const groups = useMemo(() => {
    const result = new Map();
    for (const permission of matrix.data?.permissions || []) {
      const group = permission.permission_key.split('.')[0];
      if (!result.has(group)) result.set(group, []);
      result.get(group).push(permission);
    }
    return [...result.entries()];
  }, [matrix.data]);
  const roles = matrix.data?.roles || [];
  const dirty = (role) => { const current = draft[role.role_key]; return current && (current.size !== role.permissions.length || role.permissions.some((key) => !current.has(key))); };
  const toggle = (roleKey, key) => setDraft((current) => { const next = new Set(current[roleKey]); if (next.has(key)) next.delete(key); else next.add(key); return { ...current, [roleKey]: next }; });
  const save = async (role) => {
    setSaving(role.role_key);
    try { await api.put(`/admin/roles/${role.role_key}/permissions`, { permissions: [...draft[role.role_key]] }); success(`Đã cập nhật quyền cho ${role.name}`); await matrix.reload(); } catch (error) { fail(error); } finally { setSaving(null); }
  };

  return <main className="content-page">
    <Header eyebrow="PHÂN QUYỀN" title="Vai trò và quyền" description="Backend kiểm tra từng quyền cùng phạm vi cơ sở ở mọi request; thay đổi có hiệu lực ngay và được ghi nhật ký" />
    {matrix.error && <div className="card ops-empty error">{matrix.error}</div>}
    {matrix.data && <section className="card table-card"><div className="table-wrap"><table className="permission-matrix">
      <thead><tr><th>Quyền</th>{roles.map((role) => <th key={role.role_key}>{role.name}</th>)}</tr></thead>
      <tbody>{groups.map(([group, permissions]) => [
        <tr key={group} className="group-row"><td colSpan={roles.length + 1}>{groupNames[group] || group}</td></tr>,
        ...permissions.map((permission) => <tr key={permission.permission_key}>
          <td><strong>{permission.name}</strong><small>{permission.permission_key}{permission.sensitive ? ' · nhạy cảm' : ''}</small></td>
          {roles.map((role) => <td key={role.role_key} className="check"><input type="checkbox" aria-label={`${role.name}: ${permission.name}`} disabled={role.locked}
            checked={role.locked || Boolean(draft[role.role_key]?.has(permission.permission_key))} onChange={() => toggle(role.role_key, permission.permission_key)} /></td>)}
        </tr>),
      ])}</tbody>
      <tfoot><tr><td />{roles.map((role) => <td key={role.role_key} className="check">{role.locked ? <small>Toàn quyền</small> : <Button tone="primary" disabled={!dirty(role) || saving === role.role_key} onClick={() => save(role)}><Save size={14} /> Lưu</Button>}</td>)}</tr></tfoot>
    </table></div></section>}
  </main>;
}

export function AuditPage() {
  const [filters, setFilters] = useState({ action: '', actor: '', from: '', to: '' });
  const [page, setPage] = useState(1);
  const [detail, setDetail] = useState(null);
  const list = useQuery(`/admin/audit-logs${queryString({ ...filters, page })}`, { realtime: false });
  const setFilter = (key, value) => { setFilters((current) => ({ ...current, [key]: value })); setPage(1); };
  const pretty = (value) => (value == null ? '—' : JSON.stringify(typeof value === 'string' ? JSON.parse(value) : value, null, 2));
  return <main className="content-page">
    <Header eyebrow="NHẬT KÝ" title="Nhật ký thao tác" description="Dấu vết thay đổi quan trọng: booking, điểm, kho quà, ca tiền mặt, người dùng và phân quyền" />
    <FilterBar>
      <Field label="Thao tác" value={filters.action} onChange={(value) => setFilter('action', value)} placeholder="vd: cash_shift.reopen" />
      <Field label="Người thực hiện" value={filters.actor} onChange={(value) => setFilter('actor', value)} placeholder="Tên hoặc email" />
      <Field label="Từ ngày" type="date" value={filters.from} onChange={(value) => setFilter('from', value)} />
      <Field label="Đến ngày" type="date" value={filters.to} onChange={(value) => setFilter('to', value)} />
    </FilterBar>
    <section className="card table-card">
      <Table rows={list.rows} loading={list.loading} error={list.error} onRetry={list.reload} empty="Không có nhật ký khớp bộ lọc" columns={[
        { key: 'created_at', label: 'Thời gian', render: dateTime }, { key: 'actor_name', label: 'Người thực hiện', render: (v, row) => v || row.actor_email || 'Hệ thống' },
        { key: 'action', label: 'Thao tác' }, { key: 'entity_type', label: 'Đối tượng' }, { key: 'entity_id', label: 'Mã' }, { key: 'ip_address', label: 'Địa chỉ IP' },
      ]} actions={(row) => <button className="table-action" onClick={() => setDetail(row)}>Chi tiết</button>} />
      <Pagination pagination={list.pagination} onPage={setPage} />
    </section>
    {detail && <Modal wide title={`${detail.action} · ${dateTime(detail.created_at)}`} onClose={() => setDetail(null)}>
      <div className="audit-diff"><div><h3>Trước</h3><pre>{pretty(detail.before_json)}</pre></div><div><h3>Sau</h3><pre>{pretty(detail.after_json)}</pre></div></div>
    </Modal>}
  </main>;
}
